import { Button } from "primereact/button";
import { OverlayPanel } from "primereact/overlaypanel";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import {
  FilterPanelStyled,
  FilterTriggerStyled,
  FilterValueStyled,
} from "./styled";

const PanelTitleStyled = styled.span`
  color: var(--brand-gray-700);
  font-size: 1.125rem;
  font-weight: var(--font-weight-semibold);
`;

const FieldLabelStyled = styled.label`
  color: var(--brand-gray-600);
  font-size: 0.875rem;
`;

const DateInputStyled = styled.input`
  padding: 0.625rem 0.75rem;

  border: 1px solid var(--brand-gray-200);
  border-radius: 12px;
  background-color: var(--brand-white);

  color: var(--brand-gray-700);
  font-family: var(--font-family);
  font-size: 0.875rem;

  &:focus-visible {
    outline: 2px solid var(--brand-purple-600);
    outline-offset: 2px;
  }
`;

const ClearButtonStyled = styled.button`
  cursor: pointer;

  border: none;
  background: none;

  color: var(--brand-gray-600);
  font-family: var(--font-family);
  font-size: 0.875rem;
  font-weight: var(--font-weight-semibold);

  &:hover {
    color: var(--brand-purple-700);
  }

  &:focus-visible {
    outline: 2px solid var(--brand-purple-600);
    outline-offset: 2px;
  }
`;

// `<input type="date">` speaks yyyy-MM-dd; everything outside this component
// speaks Date, matching the filter value the table already sends to the API.
const toInputValue = (date: Date | undefined) => {
  if (!date || Number.isNaN(date.getTime())) return "";
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

const fromInputValue = (value: string) => {
  if (!value) return undefined;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day);
};

const formatDate = (date: Date) =>
  `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;

export function DateRangeFilter({
  value,
  onChange,
}: {
  value: Date[];
  onChange: (dates: Date[]) => void;
}) {
  const panel = useRef<OverlayPanel>(null);
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState(toInputValue(value?.[0]));
  const [end, setEnd] = useState(toInputValue(value?.[1]));

  useEffect(() => {
    setStart(toInputValue(value?.[0]));
    setEnd(toInputValue(value?.[1]));
  }, [value]);

  const label =
    value?.[0] && value?.[1]
      ? `${formatDate(value[0])} - ${formatDate(value[1])}`
      : "All dates";

  const apply = (event: React.MouseEvent<HTMLButtonElement>) => {
    const startDate = fromInputValue(start);
    const endDate = fromInputValue(end);

    if (!startDate) {
      onChange([]);
    } else {
      onChange([startDate, endDate ?? startDate]);
    }

    panel.current?.hide();
    // PrimeReact does not fire onHide when hide() is called this way in every
    // version, so the trigger's open state is reset explicitly.
    setOpen(false);
    event.stopPropagation();
  };

  const clear = (event: React.MouseEvent<HTMLButtonElement>) => {
    setStart("");
    setEnd("");
    onChange([]);
    panel.current?.hide();
    setOpen(false);
    event.stopPropagation();
  };

  return (
    <>
      <FilterTriggerStyled
        type="button"
        data-testid="range_calendar_OrderTable"
        className="flex align-items-center gap-2"
        onClick={(e) => {
          panel.current?.toggle(e);
          setOpen((wasOpen) => !wasOpen);
        }}
      >
        <i className="pi pi-calendar" />
        Date range:
        <FilterValueStyled>{label}</FilterValueStyled>
        <i className={open ? "pi pi-chevron-up" : "pi pi-chevron-down"} />
      </FilterTriggerStyled>

      <FilterPanelStyled ref={panel} onHide={() => setOpen(false)}>
        <div className="flex align-items-center gap-2 mb-4">
          <i
            className="pi pi-calendar"
            style={{ color: "var(--brand-purple-600)" }}
          />
          <PanelTitleStyled>Select date range</PanelTitleStyled>
        </div>

        <div className="flex flex-wrap gap-3 mb-4">
          <div className="flex flex-column gap-2">
            <FieldLabelStyled htmlFor="dateRangeStart">
              Start date
            </FieldLabelStyled>
            <DateInputStyled
              id="dateRangeStart"
              data-testid="range_calendar_start_OrderTable"
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
          </div>
          <div className="flex flex-column gap-2">
            <FieldLabelStyled htmlFor="dateRangeEnd">End date</FieldLabelStyled>
            <DateInputStyled
              id="dateRangeEnd"
              data-testid="range_calendar_end_OrderTable"
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
            />
          </div>
        </div>

        <div className="flex align-items-center justify-content-between gap-3">
          <ClearButtonStyled
            type="button"
            data-testid="range_calendar_clear_OrderTable"
            onClick={clear}
          >
            Clear
          </ClearButtonStyled>
          <Button
            data-testid="range_calendar_apply_OrderTable"
            className="p-button-sm p-button-rounded-md"
            onClick={apply}
          >
            Apply dates
          </Button>
        </div>
      </FilterPanelStyled>
    </>
  );
}
